import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Chip, TextField, createFilterOptions } from '@mui/material';
import store from 'store';

import { type IStudentSimpleProgramModel } from '@common/models/StudentPrograms/StudentSimpleProgram';

import { useCalendarContext } from '../../Context';
import { StyledGradeBookMonthAutocomplete } from './styled';

const selectAllValue = 'all';

function GradeBookMonthHeaderProgramAutocomplete() {
	const { t } = useTranslation();
	const { selectedPrograms, onProgramChange } = useCalendarContext();

	const programs = store.studentPrograms;

	const allSelected = programs.length === selectedPrograms.length;

	const handleToggleOption = (selectedPrograms: IStudentSimpleProgramModel[]) =>
		onProgramChange(selectedPrograms);
	const handleClearOptions = () => onProgramChange([]);

	const handleSelectAll = (isSelected: boolean) => {
		if (isSelected) {
			onProgramChange(programs);
		} else {
			handleClearOptions();
		}
	};

	const handleToggleSelectAll = () => {
		handleSelectAll && handleSelectAll(!allSelected);
	};

	const handleChange = (
		event: React.SyntheticEvent,
		selectedOptions: IStudentSimpleProgramModel[],
		reason: string,
	) => {
		if (reason === 'selectOption' || reason === 'removeOption') {
			const selectAllOption = selectedOptions.find(
				// @ts-expect-error
				(option) => option.program_group_id === selectAllValue,
			);
			if (selectAllOption) {
				handleToggleSelectAll();

				if (allSelected) {
					handleClearOptions();
					return;
				}

				// @ts-expect-error
				const result = programs.filter((el) => el.program_group_id !== selectAllValue);
				return onProgramChange(result);
			}

			handleToggleOption && handleToggleOption(selectedOptions);
			return onProgramChange(selectedOptions);
		}

		if (reason === 'clear') {
			handleClearOptions && handleClearOptions();
		}
	};

	const filter = createFilterOptions();

	return (
		<StyledGradeBookMonthAutocomplete
			value={selectedPrograms}
			// onChange={(_, value: any) => onProgramChange(value)}
			onChange={handleChange}
			renderInput={(params) => (
				<TextField maxRows={1} placeholder={t('shared.subject')} {...params} />
			)}
			options={programs}
			getOptionLabel={(program: IStudentSimpleProgramModel) => program.program_name}
			getOptionKey={(program: IStudentSimpleProgramModel) => program.program_group_id}
			multiple
			filterOptions={(options, params) => {
				const filtered = filter(options, params);
				return [{ program_name: 'Select all', program_group_id: selectAllValue }, ...filtered];
			}}
			renderTags={(value: IStudentSimpleProgramModel[], getTagProps) => {
				const numTags = value.length;
				const limitTags = 1;

				const slicedArray = value.slice(0, limitTags);

				const className = getTagProps({ index: slicedArray.length + 1 })?.className;

				return (
					<>
						{slicedArray.map((option, index) => (
							<Chip {...getTagProps({ index })} key={index} label={option.program_name} />
						))}

						{numTags > limitTags && (
							<Chip className={className} label={` +${numTags - limitTags}`} />
						)}
					</>
				);
			}}
		/>
	);
}

export default observer(GradeBookMonthHeaderProgramAutocomplete);
